import { createContext, useContext } from 'react'
import { create, type StoreApi, type UseBoundStore } from 'zustand'
import {
    CandidateLiteDto,
    InterestedValues,
    PresentCandidateValues,
} from '../../../api/role/models/CandidateDto'
import roleApi from '../../../api/role/RoleApi'
import { AuthHelper } from '../../../auth/AuthHelper'
import { UserDto, UserRoles } from '../../../api/tenant/models/UserDto'
import {
    CandidateConnectionLiteDto,
    InterviewForThisRoleValues,
} from '../../../api/role/models/CandidateConnectionDto'
import { SetExtra } from '../../../utils/SetExtra'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import { MapExtra } from '../../../utils/MapExtra'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import personApi from '../../../api/person/PersonApi'
import companyApi from '../../../api/company/CompanyApi'
import { RoleLiteDto } from '../../../api/role/models/RoleDto'
import { CandidateFeedbackData } from '../../../api/role/models/saveHiringManagerFeedback/SaveHiringManagerFeedbackReq'
import { FeedbackValues } from '../../../api/role/models/FeedbackDto'
import mitt, { Emitter } from 'mitt'

// Define the events your emitter will handle
type CandidateEvents = {
    candidateChanged: CandidateLiteDto
}

// Create a typed emitter for candidate events
const emitter: Emitter<CandidateEvents> = mitt<CandidateEvents>()

export const ManageRoleStoreContext = createContext<UseBoundStore<
    StoreApi<ManageRoleStoreState>
> | null>(null)

type UseStoreWithEquality = {
    <U>(
        selector: (state: ManageRoleStoreState) => U,
        equalityFn?: (a: U, b: U) => boolean,
    ): U
    (): UseBoundStore<StoreApi<ManageRoleStoreState>>
}

// Overload signatures
export function useManageRoleStore<T>(
    selector: (state: ManageRoleStoreState) => T,
    equalityFn?: (a: T, b: T) => boolean,
): T
export function useManageRoleStore(): UseBoundStore<
    StoreApi<ManageRoleStoreState>
>

// Implementation
export function useManageRoleStore<T>(
    selector?: (state: ManageRoleStoreState) => T,
    equalityFn?: (a: T, b: T) => boolean,
) {
    const store = useContext(ManageRoleStoreContext)
    if (!store) {
        throw new Error(
            'useManageRoleStore must be used within a ManageRoleProvider',
        )
    }
    // Cast the store to a type that accepts the equality function.
    const typedStore = store as unknown as UseStoreWithEquality
    return selector ? typedStore(selector, equalityFn) : store
}

export interface ManageRoleStoreState {
    personLiteCache: MapExtra<string, PersonLiteDto>
    companyCache: MapExtra<string, CompanyDto>
    candidates: CandidateLiteDto[]
    orderBy: 'combined' | 'jobRelevanceOnly' | 'networkClosenessOnly'
    presentFilter: PresentCandidateValues | 'all' | 'none'
    interestedFilter: InterestedValues | 'all' | 'none'
    roleLite?: RoleLiteDto
    user?: UserDto
    selectedCandidateId?: string
    selectedCompanyId?: string
    rightPaneMode: 'role' | 'candidate' | 'network_seed' | 'company'
    presentLoadingByCandidateId: MapExtra<string, boolean>
    feedbackLoadingByCandidateId: MapExtra<string, boolean>
    fetchingCandidates: boolean
    hasMoreCandidates: boolean
    setHasMoreCandidates: (hasMoreCandidates: boolean) => void
    setFetchingCandidates: (fetching: boolean) => void
    setOrderBy: (
        orderBy: 'combined' | 'jobRelevanceOnly' | 'networkClosenessOnly',
    ) => void
    setPresentFilter: (
        presentFilter: PresentCandidateValues | 'all' | 'none',
    ) => void
    setInterestedFilter: (
        interestedFilter: InterestedValues | 'all' | 'none',
    ) => void
    setUser: (user: UserDto) => void
    setRoleLite: (roleLite: RoleLiteDto) => void
    setCandidates: (candidates: CandidateLiteDto[]) => void
    clearCandidates: () => void
    fetchNextCandidates: (params?: { quantity?: number }) => Promise<void>
    primePersonCache(personIds: string[]): Promise<void>
    primeCompanyCache(companyIds: string[]): Promise<void>
    getUserSafe(): UserDto
    safeGetRoleLite(): RoleLiteDto
    selectCandidateId(candidateId: string): void
    selectCompanyId(companyId: string): void
    setRightPaneMode: (
        mode: 'role' | 'candidate' | 'network_seed' | 'company',
    ) => void
    updatePresentForCandidate(
        candidateId: string,
        value?: PresentCandidateValues,
    ): Promise<void>
    updateContactedForCandidate(
        candidateId: string,
        contacted: boolean,
        localOnly?: boolean,
    ): Promise<void>
    updateInterestedForCandidate(
        candidateId: string,
        value?: InterestedValues,
    ): Promise<void>
    updateInterviewForCandidate(
        candidateId: string,
        value?: InterviewForThisRoleValues,
    ): Promise<void>
    updateFeedbackForCandidate(
        candidateFeedbackData: CandidateFeedbackData,
    ): Promise<void>
    localUpdateFeedbackComment(
        candidateId: string,
        feedbackComment: string,
    ): void
    updateFeedbackValueForCandidate(
        candidateId: string,
        value: FeedbackValues,
    ): Promise<void>
    updateFeedbackCommentForCandidate(candidateId: string): Promise<void>
    onCandidateChanged: (
        callback: (updatedCandidate: CandidateLiteDto) => void,
    ) => () => void
    addCandidateToRole: (candidate: CandidateLiteDto) => Promise<void>
    removeCandidateFromRole: (candidateId: string) => Promise<void>
}

export const createManageRoleStore = () =>
    create<ManageRoleStoreState>((set, get) => ({
        personLiteCache: new MapExtra<string, PersonLiteDto>(),
        companyCache: new MapExtra<string, CompanyDto>(),
        candidates: [],
        orderBy: 'combined',
        presentFilter: 'all',
        interestedFilter: 'all',
        rightPaneMode: 'role',
        presentLoadingByCandidateId: new MapExtra<string, boolean>(),
        feedbackLoadingByCandidateId: new MapExtra<string, boolean>(),
        fetchingCandidates: false,
        hasMoreCandidates: true,
        setHasMoreCandidates: (hasMoreCandidates) => set({ hasMoreCandidates }),
        setFetchingCandidates: (fetching) =>
            set({ fetchingCandidates: fetching }),
        localUpdateFeedbackComment: (candidateId, feedbackComment) => {
            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const updatedCandidates = state.candidates.map((candidate) => {
                if (candidate.candidatePersonId !== candidateId)
                    return candidate

                return {
                    ...candidate,
                    connections: candidate.connections.map((connection) => {
                        if (connection.connectedPersonId !== hmPersonId)
                            return connection

                        return {
                            ...connection,
                            feedback: {
                                ...connection.feedback,
                                comment: feedbackComment,
                                value:
                                    connection.feedback?.value ??
                                    FeedbackValues.HIDDEN,
                            },
                        }
                    }),
                }
            })

            set({ candidates: updatedCandidates })
        },
        updateInterviewForCandidate: async (
            candidateId: string,
            value?: InterviewForThisRoleValues,
        ) => {
            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const candidate = state.candidates.find(
                (candidate) => candidate.candidatePersonId === candidateId,
            )
            if (!candidate) {
                throw new Error(
                    `updateInterviewForCandidate: Candidate not found for id: ${candidateId}`,
                )
            }

            const candidateConn = candidate.connections.find(
                (connection) => connection.connectedPersonId === hmPersonId,
            )
            if (!candidateConn) {
                throw new Error(
                    `updateInterviewForCandidate: Connection not found for candidateId: ${candidateId} and hmId: ${hmPersonId}`,
                )
            }

            await state.updateFeedbackForCandidate({
                candidateId,
                feedback: {
                    value:
                        candidateConn.feedback?.value ?? FeedbackValues.HIDDEN,
                    comment: candidateConn.feedback?.comment,
                },
                interviewForThisRole: value,
            })

            // Update the in-memory candidate with the new feedback value
            set((state) => ({
                candidates: state.candidates.map((candidate) =>
                    candidate.candidatePersonId === candidateId
                        ? {
                              ...candidate,
                              connections: candidate.connections.map(
                                  (connection) =>
                                      connection.connectedPersonId ===
                                      hmPersonId
                                          ? {
                                                ...connection,
                                                interviewForThisRole: value,
                                            }
                                          : connection,
                              ),
                          }
                        : candidate,
                ),
            }))
        },
        updateFeedbackCommentForCandidate: async (candidateId) => {
            // the comment has already been changed in the local state

            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const candidate = state.candidates.find(
                (candidate) => candidate.candidatePersonId === candidateId,
            )
            if (!candidate) {
                throw new Error(
                    `updateFeedbackCommentForCandidate: Candidate not found for id: ${candidateId}`,
                )
            }

            const candidateConn = candidate.connections.find(
                (connection) => connection.connectedPersonId === hmPersonId,
            )
            if (!candidateConn) {
                throw new Error(
                    `updateFeedbackCommentForCandidate: Connection not found for candidateId: ${candidateId} and hmId: ${hmPersonId}`,
                )
            }

            await state.updateFeedbackForCandidate({
                candidateId,
                feedback: {
                    value:
                        candidateConn.feedback?.value ?? FeedbackValues.HIDDEN,
                    comment: candidateConn.feedback?.comment,
                },
                interviewForThisRole: candidateConn.interviewForThisRole,
            })
        },
        updateFeedbackValueForCandidate: async (candidateId, value) => {
            const state = get()
            const hmPersonId = state.getUserSafe().personId!

            const candidate = state.candidates.find(
                (candidate) => candidate.candidatePersonId === candidateId,
            )
            if (!candidate) {
                throw new Error(
                    `updateFeedbackValueForCandidate: Candidate not found for id: ${candidateId}`,
                )
            }

            const candidateConn = candidate.connections.find(
                (connection) => connection.connectedPersonId === hmPersonId,
            )
            if (!candidateConn) {
                throw new Error(
                    `updateFeedbackValueForCandidate: Connection not found for candidateId: ${candidateId} and hmId: ${hmPersonId}`,
                )
            }

            await state.updateFeedbackForCandidate({
                candidateId,
                feedback: {
                    value,
                    comment: candidateConn.feedback?.comment,
                },
                interviewForThisRole: candidateConn.interviewForThisRole,
            })

            // Update the in-memory candidate with the new feedback value
            set((state) => ({
                candidates: state.candidates.map((candidate) =>
                    candidate.candidatePersonId === candidateId
                        ? {
                              ...candidate,
                              connections: candidate.connections.map(
                                  (connection) =>
                                      connection.connectedPersonId ===
                                      hmPersonId
                                          ? {
                                                ...connection,
                                                feedback: {
                                                    ...connection.feedback,
                                                    value, // Updated feedback value
                                                },
                                            }
                                          : connection,
                              ),
                          }
                        : candidate,
                ),
            }))
        },

        updateFeedbackForCandidate: async (
            candidateFeedbackData: CandidateFeedbackData,
        ) => {
            const state = get()

            set((state) => {
                const newMap = new MapExtra<string, boolean>(
                    state.feedbackLoadingByCandidateId.entries(),
                )
                newMap.set(candidateFeedbackData.candidateId, true)
                return { feedbackLoadingByCandidateId: newMap }
            })

            try {
                await roleApi.saveHiringManagerFeedbackForCandidate({
                    roleId: state.safeGetRoleLite().id,
                    hiringManagerId: state.getUserSafe().personId!,
                    candidateFeedbackData,
                })
            } finally {
                set((state) => {
                    const newMap = new MapExtra<string, boolean>(
                        state.feedbackLoadingByCandidateId.entries(),
                    )
                    newMap.set(candidateFeedbackData.candidateId, false)
                    return { feedbackLoadingByCandidateId: newMap }
                })
            }
        },
        onCandidateChanged: (
            callback: (updatedCandidate: CandidateLiteDto) => void,
        ): (() => void) => {
            emitter.on('candidateChanged', callback)
            return () => emitter.off('candidateChanged', callback)
        },
        updatePresentForCandidate: async (
            personId: string,
            value?: PresentCandidateValues,
        ) => {
            const stateVar = get()

            set((state) => {
                const newMap = new MapExtra<string, boolean>(
                    state.presentLoadingByCandidateId.entries(),
                )
                newMap.set(personId, true)
                return { presentLoadingByCandidateId: newMap }
            })

            try {
                // const presentCandidateValue =
                //     value === 'not_selected'
                //         ? undefined
                //         : safeCastToEnum(value, PresentCandidateValues)

                await roleApi.setCandidatePresentValue({
                    candidateId: personId,
                    roleId: stateVar.safeGetRoleLite().id,
                    presentCandidateValue: value,
                })

                set((state) => ({
                    candidates: state.candidates.map((candidate) => {
                        if (candidate.candidatePersonId === personId) {
                            const updatedCandidate = {
                                ...candidate,
                                present: value,
                            }
                            // Emit the event with the updated candidate as payload.
                            emitter.emit('candidateChanged', updatedCandidate)
                            return updatedCandidate
                        }
                        return candidate
                    }),
                }))
            } finally {
                set((state) => {
                    const newMap = new MapExtra<string, boolean>(
                        state.presentLoadingByCandidateId.entries(),
                    )
                    newMap.set(personId, false)
                    return { presentLoadingByCandidateId: newMap }
                })
            }
        },
        updateContactedForCandidate: async (
            personId: string,
            contacted: boolean,
            localOnly: boolean = false,
        ) => {
            const stateVar = get()

            if (!localOnly) {
                // TODO: we need to have the server respond with the date that it used for the
                //  interestedDate field
                await roleApi.setCandidateContactedValue({
                    candidateId: personId,
                    roleId: stateVar.safeGetRoleLite().id,
                    contacted,
                })
            }

            set((state) => ({
                candidates: state.candidates.map((candidate) => {
                    if (candidate.candidatePersonId === personId) {
                        const updatedCandidate = {
                            ...candidate,
                            contacted,
                        }
                        // Emit the event with the updated candidate as payload.
                        emitter.emit('candidateChanged', updatedCandidate)
                        return updatedCandidate
                    }
                    return candidate
                }),
            }))
        },
        updateInterestedForCandidate: async (
            personId: string,
            value?: InterestedValues,
        ) => {
            const stateVar = get()

            // TODO: we need to have the server respond with the date that it used for the
            //  interestedDate field
            await roleApi.setCandidateInterestedValue({
                candidateId: personId,
                roleId: stateVar.safeGetRoleLite().id,
                interested: value,
            })

            set((state) => ({
                candidates: state.candidates.map((candidate) => {
                    if (candidate.candidatePersonId === personId) {
                        const updatedCandidate = {
                            ...candidate,
                            interested: value,
                        }
                        // Emit the event with the updated candidate as payload.
                        emitter.emit('candidateChanged', updatedCandidate)
                        return updatedCandidate
                    }
                    return candidate
                }),
            }))
        },
        setRightPaneMode: (mode) => set({ rightPaneMode: mode }),
        setOrderBy: (orderBy) => set({ orderBy }),
        setPresentFilter: (presentFilter) => set({ presentFilter }),
        setInterestedFilter: (interestedFilter) => set({ interestedFilter }),
        setUser: (user) => set({ user }),
        setRoleLite: (roleLite) => set({ roleLite }),
        selectCandidateId: (candidateId) =>
            set({ selectedCandidateId: candidateId }),
        selectCompanyId: (companyId) => set({ selectedCompanyId: companyId }),
        getUserSafe: (): UserDto => {
            const state = get()
            if (!state.user) {
                throw new Error('User not set in ManageRoleStore')
            }
            return state.user
        },
        safeGetRoleLite: (): RoleLiteDto => {
            const state = get()
            if (!state.roleLite) {
                throw new Error('RoleLite not set in ManageRoleStore')
            }
            return state.roleLite
        },
        setCandidates: (candidates) => set({ candidates }),
        clearCandidates: () => set({ candidates: [] }),
        fetchNextCandidates: async ({
            quantity = 50,
        }: { quantity?: number } = {}) => {
            const state = get()
            const roleId = state.safeGetRoleLite().id

            state.setFetchingCandidates(true)

            try {
                const pageNumber =
                    Math.ceil(state.candidates.length / quantity) + 1

                // Insert your API call and candidate transformation logic here.
                let newCandidateLites: CandidateLiteDto[] = (
                    await roleApi.getCandidates({
                        roleId,
                        pageSize: 50,
                        pageNumber,
                        orderBy: state.orderBy,
                        presentFilter: state.presentFilter,
                        interestedFilter: state.interestedFilter,
                    })
                ).candidates

                // TODO: this filtering needs to be done server side really
                // If the user is not a TA, filter the candidates to only show those that have a connection to the user
                const user = state.getUserSafe()
                if (!AuthHelper.userHasRole(user, UserRoles.TA)) {
                    newCandidateLites = newCandidateLites.filter((candidate) =>
                        candidate.connections.some(
                            (conn) => conn.connectedPersonId === user.personId,
                        ),
                    )

                    // Show first the connections that are relevant to the user
                    newCandidateLites = newCandidateLites.map((candidate) => {
                        const { relevantConnections, otherConnections } =
                            candidate.connections.reduce(
                                (acc, connection) => {
                                    if (
                                        connection.connectedPersonId ===
                                        user.personId
                                    ) {
                                        acc.relevantConnections.push(connection)
                                    } else {
                                        acc.otherConnections.push(connection)
                                    }
                                    return acc
                                },
                                {
                                    relevantConnections: [],
                                    otherConnections: [],
                                } as {
                                    relevantConnections: CandidateConnectionLiteDto[]
                                    otherConnections: CandidateConnectionLiteDto[]
                                },
                            )

                        return {
                            ...candidate,
                            connections: [
                                ...relevantConnections,
                                ...otherConnections,
                            ],
                        }
                    })
                }

                // Update caches for persons and companies only on the first page or for newly loaded candidates.
                const personIds = new SetExtra<string>(
                    newCandidateLites.flatMap((candidate) => [
                        candidate.candidatePersonId,
                        ...candidate.connections.map(
                            (conn) => conn.connectedPersonId,
                        ),
                    ]),
                )

                await state.primePersonCache(personIds.getValuesAsArray())

                const companyIds = new SetExtra<string>(
                    newCandidateLites.flatMap((candidate) =>
                        candidate.connections.flatMap((conn) =>
                            conn.sharedWorkHistory.flatMap(
                                (history) => history.companyId,
                            ),
                        ),
                    ),
                )

                // also add the companies from the company links
                for (const candidate of newCandidateLites) {
                    for (const companyConnection of candidate.companyConnections) {
                        companyIds.add(companyConnection.connectedCompanyId)
                    }
                }

                await state.primeCompanyCache(companyIds.getValuesAsArray())

                // Example: replacing the candidate list for page 1 or appending for subsequent pages.
                set((state) => ({
                    candidates:
                        pageNumber === 1
                            ? newCandidateLites
                            : [...state.candidates, ...newCandidateLites],
                }))

                state.setHasMoreCandidates(
                    newCandidateLites.length === quantity,
                )
            } finally {
                state.setFetchingCandidates(false)
            }
        },
        primePersonCache: async (personIds: string[]): Promise<void> => {
            const state = get()

            // ensure we don't prime the cache with ids that are already in the cache
            const idsToPrime = personIds.filter(
                (id) => !state.personLiteCache.has(id),
            )

            if (idsToPrime.length === 0) {
                return
            }

            const persons = (
                await personApi.getPersonsLite({
                    personIds: idsToPrime,
                })
            ).persons

            for (const person of persons) {
                state.personLiteCache.set(person.id, person)
            }
        },
        primeCompanyCache: async (companyIds: string[]): Promise<void> => {
            const state = get()

            // ensure we don't prime the cache with ids that are already in the cache
            const idsToPrime = companyIds.filter(
                (id) => !state.companyCache.has(id),
            )

            if (idsToPrime.length === 0) {
                return
            }

            const companies = (
                await companyApi.getCompanies({
                    companyIds: idsToPrime,
                })
            ).companies

            for (const company of companies) {
                state.companyCache.set(company.id, company)
            }
        },
        addCandidateToRole: async (candidate): Promise<void> => {
            const state = get()

            await state.primePersonCache([candidate.candidatePersonId])

            set((s) => ({
                candidates: [...s.candidates, candidate],
            }))

            emitter.emit('candidateChanged', candidate)
        },

        removeCandidateFromRole: async (candidateId: string): Promise<void> => {
            set((state) => ({
                candidates: state.candidates.filter(
                    (c) => c.candidatePersonId !== candidateId,
                ),
            }))
        },
    }))
